import { useRef } from 'react';

import { useModal } from 'mui-modal-provider';
import pluralize from 'pluralize';

import { parseApiError } from '@cast/api-client';
import {
  Button,
  Dialog,
  DialogProps,
  notify,
  ShowModalOutput,
  Typography,
  Stack,
} from '@cast/design-system';
import { Cluster, Node } from '@cast/types';

import { isKentManagedCluster } from '@cast/shared-cluster-context-data-access';
import { NonEmptyArray } from '@cast/shared-types';
import { RemoveCenteredLayout } from './_components/RemoveCenteredLayout';
import {
  useDeleteNodeMutation,
  useDeleteNodesMutation,
} from '../../../mutations/nodes';
import { useKentDeleteNodeMutation } from 'hooks/mutations/kent/useKentDeleteNodeMutation';
import { useKentDeleteNodesMutation } from './_hooks/useKentDeleteNodesMutation';

type Props = DialogProps & {
  cluster: Cluster;
  nodes: NonEmptyArray<Node>;
  onClose: () => void;
  onDeleted?: (deletedNodeIds: string[]) => void;
};

const DeleteNodeDialog = ({
  cluster,
  nodes,
  onClose,
  onDeleted,
  ...props
}: Props) => {
  const deleteNodesMutation = useDeleteNodesMutation(cluster.id);
  const deleteNodeMutation = useDeleteNodeMutation(cluster.id);
  const kentDeleteNodeMutation = useKentDeleteNodeMutation();
  const kentDeleteNodesMutation = useKentDeleteNodesMutation();

  const isKentManaged = isKentManagedCluster(cluster);
  const multiple = nodes.length > 1;
  const isMutating =
    deleteNodeMutation.isPending ||
    deleteNodesMutation.isPending ||
    kentDeleteNodeMutation.isPending ||
    kentDeleteNodesMutation.isPending;

  const handleDeleteKentNodes = () => {
    if (multiple) {
      kentDeleteNodesMutation.mutate(
        {
          clusterId: cluster.id,
          nodeNames: nodes.map((node) => node.name),
        },
        {
          onSuccess: (results) => {
            const deletedNodeNames = new Set(
              results
                .filter((result) => result.success)
                .map((result) => result.nodeName)
            );
            onDeleted?.(
              nodes
                .filter((node) => deletedNodeNames.has(node.name))
                .map((node) => node.id)
            );
            onClose();
          },
        }
      );
      return;
    }

    const node = nodes[0];
    kentDeleteNodeMutation.mutate(
      { clusterId: cluster.id, nodeName: node.name, nodeId: node.id },
      {
        onSuccess: () => {
          notify.success('Node deletion initiated');
          onDeleted?.([node.id]);
          onClose();
        },
        onError: (error) => {
          notify.error(parseApiError(error, 'Failed to initiate deletion'));
        },
      }
    );
  };

  const handleDeleteAutoscalerNodes = () => {
    if (multiple) {
      deleteNodesMutation.mutate(
        nodes.map((node) => node.id),
        {
          onSuccess: (results) => {
            onDeleted?.(
              results
                .filter((result) => result.success)
                .map((result) => result.id)
            );
            onClose();
          },
        }
      );
      return;
    }

    const node = nodes[0];
    deleteNodeMutation.mutate(node.id, {
      onSuccess: () => {
        onDeleted?.([node.id]);
        onClose();
      },
    });
  };

  const handleDelete = () => {
    if (isKentManaged) {
      handleDeleteKentNodes();
      return;
    }
    handleDeleteAutoscalerNodes();
  };

  return (
    <Dialog
      {...props}
      onClose={onClose}
      PaperProps={{ sx: { overflow: 'visible' } }}
      testId="delete-nodes-dialog"
    >
      <RemoveCenteredLayout
        heading={
          multiple ? (
            <Typography variant="h4" align="center">
              Delete {nodes.length} {pluralize('node', nodes.length)}?
            </Typography>
          ) : (
            <div>
              <Typography variant="h4" align="center">
                Delete node?
              </Typography>
              <Typography variant="h6" align="center" noWrap>
                {nodes[0].name}
              </Typography>
            </div>
          )
        }
        description={
          <Typography variant="P12R" align="center" color="textSecondary">
            {multiple
              ? 'Selected nodes will be drained and deleted. Depending on your application configuration, this action may cause downtime.'
              : 'The node will be drained and deleted. Depending on your application configuration, this action may cause downtime.'}
          </Typography>
        }
        actions={
          <Stack direction="row" gap="16px">
            <Button
              variant="secondary"
              onClick={onClose}
              testId="cancel-node-delete"
            >
              Cancel
            </Button>

            <Button
              variant="danger"
              onClick={handleDelete}
              loading={isMutating}
              testId="confirm-node-delete"
            >
              Delete
            </Button>
          </Stack>
        }
      />
    </Dialog>
  );
};

export const useDeleteNodeDialog = () => {
  const { showModal } = useModal();
  const modalInstance = useRef<ShowModalOutput>(undefined);

  const openDeleteNodeDialog = ({
    cluster,
    nodes,
    onDeleted,
  }: {
    cluster: Cluster;
    nodes: NonEmptyArray<Node>;
    onDeleted?: (deletedNodeIds: string[]) => void;
  }) => {
    modalInstance.current = showModal(DeleteNodeDialog, {
      nodes,
      cluster,
      onDeleted,
      onClose: () => modalInstance.current?.destroy(),
    });
  };

  return { openDeleteNodeDialog };
};
